import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Mojo Journey tracker.
 *
 * Feb-2026 update — the Franchisee tracker renders Sandra's full-card
 * SVGs directly (icon + border + background + baked-in label all live
 * inside the SVG). We no longer wrap them in an HTML/CSS tile. State
 * drives which colour variant is loaded (orange/green/grey).
 *
 * The Interested tracker still uses the older HTML+PNG tile approach
 * pending Sandra's supplied "Interested" SVG set — do not touch that
 * path until the artwork arrives.
 */

// Backend step-id → full-card SVG filename (Interested journey).
// User-supplied artwork with the icon + border + background + label
// baked into each SVG (viewBox 199.2 × 81.2). Rendered directly as
// an <img> — never rebuild the label in HTML or add another card
// behind the SVG.
const INTERESTED_SVG_FILE = {
  discovery_portal_sent:     "discovery_sent.svg",
  shadow_day_setup:          "shadow_day_setup.svg",
  training_day_booked:       "training_day_booked.svg",
  territory_sent:            "territory_defined_and_sent.svg",
  confidentiality_agreement: "confidential_agreement_signed.svg",
  flex_information:          "flex_info_requested.svg",
};

function interestedSvgSrc(stepId, state) {
  const file = INTERESTED_SVG_FILE[stepId];
  if (!file) return "";
  const color = state === "done" ? "green" : state === "na" ? "grey" : "orange";
  return `/journey-icons/svg/interested/${color}/${file}`;
}

// Backend step-id → user-supplied full-card SVG filename (Franchisee).
const FRANCHISEE_SVG_FILE = {
  launch_portal_provided:   "launch_portal_provided.svg",
  email_address_setup:      "email_address_setup.svg",
  portal_account_setup:     "portal_setup.svg",
  mojo_business_facebook:   "business_facebook.svg",
  franchisee_facebook:      "franchise_facebook.svg",
  comfort_zone_invite:      "facebook_comfort_zone.svg",
  contract_provided:        "contract_provided.svg",
  contract_length_and_plan: "contract_length_and_plan.svg",
  territory_name_confirmed: "territory_name_confirmed.svg",
  direct_debit_setup:       "direct_debit_setup.svg",
  dbs_processed:            "dbs_processed.svg",
  trolley_and_art:          "trolleyart_materials.svg",
  apron_ordered:            "apron_ordered.svg",
  printed_items:            "printed_items_done.svg",
};

function franchiseeSvgSrc(stepId, state) {
  const file = FRANCHISEE_SVG_FILE[stepId];
  if (!file) return "";
  const color = state === "done" ? "green" : state === "na" ? "grey" : "orange";
  return `/journey-icons/svg/${color}/${file}`;
}

// Two-line label breaks removed — Interested labels now live inside
// the user-supplied SVGs (feb-2026 artwork refresh).

// Cycle order depends on whether N/A is permitted on this step.
// off → done → na → off  when allowed
// off → done → off       when not allowed
function nextState(current, naAllowed) {
  if (current === "off") return "done";
  if (current === "done") return naAllowed ? "na" : "off";
  return "off"; // from "na" (only reachable when allowed)
}

// Feb-2026 audit: per-card scroll anchors. Derived cards deep-link
// to the specific Launch Portal sub-section that owns their source
// data (testids come from `FranchiseeLaunchPortalPanel.jsx` — the
// admin panel that sits directly below the tracker). If a card has
// no sub-section to jump to (Portal Setup lives on the franchisee
// page header, not inside the Launch Portal panel), we fall back
// to the parent section-launch-portal wrapper.
const SECTION_ANCHOR = {
  email_address_setup:      "section-email-account",
  portal_account_setup:     "section-launch-portal", // Enable Portal lives on the franchisee page header
  franchisee_facebook:      "section-facebook",
  comfort_zone_invite:      "section-comfort-zone",
  contract_provided:        "section-contract-details",
  contract_length_and_plan: "section-contract-details",
  territory_name_confirmed: "section-franchise-name",
  direct_debit_setup:       "section-direct-debit",
  dbs_processed:            "section-dbs",
};

const OUTSTANDING_ACTION = {
  email_address_setup:      "Tick HQ Complete in the Email Account section once the mailbox is live.",
  portal_account_setup:     "Use the Enable Portal action at the top of the franchisee record.",
  franchisee_facebook:      "Tick HQ Complete in the Personalised Facebook Page Setup section.",
  comfort_zone_invite:      "Tick HQ Complete in the Creative Mojo Comfort Zone section.",
  contract_provided:        "Issue this franchisee's contract from the Contracts register — a sample doesn't count.",
  contract_length_and_plan: "The franchisee needs to submit Contract Term and Franchise Fee Structure in their Launch Portal.",
  territory_name_confirmed: "The franchisee needs to complete Your Franchise Name in their Launch Portal.",
  direct_debit_setup:       "Tick HQ Complete in the Direct Debit section once the mandate is confirmed.",
  dbs_processed:            "Tick HQ Complete in the DBS section once the certificate lands.",
};

function scrollToSection(sectionTestId) {
  const el = document.querySelector(`[data-testid="${sectionTestId}"]`);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.classList.add("ring-2", "ring-emerald-400/60", "rounded-xl");
  setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400/60", "rounded-xl"), 1800);
  return true;
}

const BADGE_TXT = { off: "", done: "✓", na: "N/A" };
const BADGE_CLS = {
  done: "bg-emerald-500 text-white",
  na:   "bg-slate-400 text-white text-[9px] px-1.5",
};
const GROUP_ORDER_INTERESTED = ["Information", "Contract & Territory"];
const GROUP_ORDER_FRANCHISEE = ["Digital", "Contract & Territory", "Finance & DBS", "Materials"];

export default function MojoJourney({
  journey,               // "interested" | "franchisee"
  entityId,              // contact_id or franchisee_id
  title,
  hideTitle = false,
  onChange,
}) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyStepId, setBusyStepId] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/admin/mojo-journey/${journey}/${entityId}`);
      setState(data);
      onChange?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load Mojo Journey");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey, entityId]);

  useEffect(() => { load(); }, [load]);

  const cycleStep = useCallback(async (step) => {
    if (!state || busyStepId) return;
    setBusyStepId(step.id);
    const ns = nextState(step.state, step.na_allowed);
    let body;
    if (step.manual) {
      body = { state: ns, na: ns === "na" };
    } else {
      // Derived steps only support the na overlay.
      body = { na: ns === "na" };
    }
    try {
      const { data } = await api.patch(
        `/admin/mojo-journey/${journey}/${entityId}/steps/${step.id}`, body,
      );
      setState(data);
      onChange?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Update failed");
    } finally {
      setBusyStepId(null);
    }
  }, [busyStepId, state, journey, entityId, onChange]);

  // Direct N/A toggle for derived steps — bypasses the cycle-order
  // helper so a single click from the toast action button flips the
  // overlay in exactly one API round-trip. Never touches source data.
  const setNa = useCallback(async (step, na) => {
    if (busyStepId) return;
    setBusyStepId(step.id);
    try {
      const { data } = await api.patch(
        `/admin/mojo-journey/${journey}/${entityId}/steps/${step.id}`,
        { na },
      );
      setState(data);
      onChange?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Update failed");
    } finally {
      setBusyStepId(null);
    }
  }, [busyStepId, journey, entityId, onChange]);

  // Derived cards: click = scroll to the SPECIFIC section that owns
  // the source data + toast the outstanding action. For N/A-allowed
  // derived cards the toast carries a "Flag N/A" / "Remove N/A"
  // action button so HQ can opt the step out without touching source.
  const handleDerivedClick = useCallback((step) => {
    const anchor = SECTION_ANCHOR[step.id] || "section-launch-portal";
    const jumped = scrollToSection(anchor);
    const detail = OUTSTANDING_ACTION[step.id] || "This step is derived from source data — no manual override.";
    const naAction = step.na_allowed
      ? { action: {
            label: step.state === "na" ? "Remove N/A" : "Flag N/A",
            onClick: () => setNa(step, step.state !== "na"),
        } }
      : undefined;
    const jumpText = jumped
      ? (anchor === "section-launch-portal"
          ? "Jumped to the Launch Portal section."
          : `Jumped to the ${anchor.replace(/^section-/, "").replace(/-/g, " ")} section.`)
      : undefined;
    if (step.state === "done") {
      toast.success(`${step.label} — already complete.${step.supp ? ` (${step.supp})` : ""}`, {
        description: jumpText,
        ...naAction,
      });
    } else if (step.state === "na") {
      toast(`${step.label} — flagged N/A`, {
        description: "This step is excluded from the completion %.",
        ...naAction,
      });
    } else {
      toast(`${step.label} — outstanding`, {
        description: `${detail}${jumpText ? " " + jumpText : ""}`,
        ...naAction,
      });
    }
  }, [setNa]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const s of (state?.steps || [])) {
      const key = s.group || "Steps";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(s);
    }
    const order = journey === "franchisee" ? GROUP_ORDER_FRANCHISEE : GROUP_ORDER_INTERESTED;
    return order.filter((k) => g.has(k)).map((k) => [k, g.get(k)]);
  }, [state, journey]);

  const done = state?.steps_done || 0;
  const considered = state?.steps_considered || 0;
  const naCount = state?.steps_na || 0;
  const pct = state?.percent_complete ?? 0;

  const isFranchisee = journey === "franchisee";
  // Interested cards are supplied at 199.2 × 81.2. They render at
  // their intrinsic aspect ratio (see InterestedCard below), so the
  // fixed-tile dimensions here are only used by the Franchisee path.
  const tileW = isFranchisee ? 84 : undefined;
  const tileH = isFranchisee ? 80 : undefined;

  if (!entityId) return null;
  if (loading && !state) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500"
           data-testid="mojo-journey-loading">Loading Mojo Journey…</div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
           data-testid="mojo-journey-error">
        <div className="font-medium mb-1">Mojo Journey unavailable</div>
        <div className="opacity-80">{String(error)}</div>
      </div>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"
             data-testid={`mojo-journey-${journey}`}>
      {!hideTitle && (
        <header className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-widest">
              {title || (journey === "interested" ? "Interested Journey" : "Franchisee Journey")}
            </h3>
            <span className="text-xs text-slate-500">
              {done}/{considered} complete{naCount ? ` · ${naCount} N/A` : ""}
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-[140px]">
            <div className="h-2 flex-1 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
                   style={{ width: `${pct}%` }} data-testid="mojo-journey-progress-bar" />
            </div>
            <div className="text-sm font-semibold text-slate-900 w-10 text-right"
                 data-testid="mojo-journey-progress-pct">{pct}%</div>
          </div>
        </header>
      )}

      {/* Single-row layout, horizontal scroll on narrow viewports.
          Pad the scroll wrapper by 12 px on each side (and 8 px top)
          so the ✓ / N/A badges — which sit 6 px outside the card at
          top-right — don't trigger a spurious horizontal scrollbar
          when every step ends up ticked. Negative margins keep the
          visual alignment unchanged. */}
      <div className="overflow-x-auto -mx-3 px-3 -mt-2 pt-2">
        <div className={`flex flex-nowrap items-start w-max ${isFranchisee ? "mx-auto" : ""}`} style={{ columnGap: isFranchisee ? 10 : 14 }}>
          {grouped.map(([groupName, steps]) => (
            <div key={groupName}
                 className="flex-none min-w-0"
                 data-testid={`mojo-journey-group-${groupName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="mb-2">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">{groupName}</div>
                <div className="mt-1 h-1.5 w-full rounded-full" style={{ backgroundColor: "#dedd0c" }} />
              </div>
              <div className="flex flex-nowrap" style={{ gap: isFranchisee ? 8 : 14 }}>
                {steps.map((s) => (
                  isFranchisee
                    ? <FranchiseeCard key={s.id} step={s} busy={busyStepId === s.id}
                                     onClick={() => s.manual ? cycleStep(s) : handleDerivedClick(s)} />
                    : <InterestedCard key={s.id} step={s} busy={busyStepId === s.id}
                                     onClick={() => s.manual ? cycleStep(s) : handleDerivedClick(s)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Franchisee card — pure <img> render at 84×80 --------

function FranchiseeCard({ step, busy, onClick }) {
  const nextLabel = nextState(step.state, step.na_allowed);
  const tip = step.manual
    ? `${step.label} · click cycles to ${nextLabel.toUpperCase()}`
    : `${step.label} — derived · click jumps to Launch Portal${step.na_allowed ? " · toast offers a Flag N/A action" : ""}`;
  return (
    <button
      type="button"
      className="relative flex-none block p-0 border-0 bg-transparent disabled:opacity-70 hover:-translate-y-0.5 transition"
      onClick={onClick}
      disabled={busy}
      title={tip}
      data-testid={`mojo-journey-step-${step.id}`}
      data-state={step.state}
      data-manual={step.manual ? "yes" : "no"}
      data-na-allowed={step.na_allowed ? "yes" : "no"}
      style={{ width: 80, height: 76 }}
    >
      <img
        src={franchiseeSvgSrc(step.id, step.state)}
        alt={step.label}
        style={{ width: 80, height: 76, display: "block" }}
        draggable={false}
      />
      {step.state !== "off" && (
        <span
          className={`absolute -top-1.5 -right-1.5 h-[16px] min-w-[16px] px-1 rounded-full inline-flex items-center justify-center text-[8px] font-bold shadow ring-2 ring-white ${BADGE_CLS[step.state]}`}
        >
          {BADGE_TXT[step.state]}
        </span>
      )}
    </button>
  );
}

// -------- Interested card — pure <img> render at 200 × 81.4 --------
//
// Fixed 200 px wide so the yellow group rule (which spans the group
// column's width) lines up exactly with the cards row below it —
// group column width = 3 × 200 + 2 × 14 = 628 px. Aspect ratio is
// preserved via the intrinsic SVG viewBox (199.2 / 81.2).

function InterestedCard({ step, busy, onClick }) {
  const tip = step.manual
    ? `${step.label} · click cycles state`
    : `${step.label} — derived · click jumps to the relevant section`;
  return (
    <button
      type="button"
      className="relative flex-none block p-0 border-0 bg-transparent disabled:opacity-70 hover:-translate-y-0.5 transition"
      onClick={onClick}
      disabled={busy}
      title={tip}
      data-testid={`mojo-journey-step-${step.id}`}
      data-state={step.state}
      data-manual={step.manual ? "yes" : "no"}
      data-na-allowed={step.na_allowed ? "yes" : "no"}
      style={{ width: 200, aspectRatio: "199.2 / 81.2" }}
    >
      <img
        src={interestedSvgSrc(step.id, step.state)}
        alt={step.label}
        style={{ width: "100%", height: "100%", display: "block" }}
        draggable={false}
      />
      {step.state !== "off" && (
        <span
          className={`absolute -top-1.5 -right-1.5 h-[16px] min-w-[16px] px-1 rounded-full inline-flex items-center justify-center text-[8px] font-bold shadow ring-2 ring-white ${BADGE_CLS[step.state]}`}
        >
          {BADGE_TXT[step.state]}
        </span>
      )}
    </button>
  );
}
